import { useState, useEffect, useCallback } from 'react';
import { useDebounce } from '@/hooks/use-debounce';
import { rolloverService } from '@/services/leave/rolloverService';
import { useAuth } from '@/hooks/useAuth';
import { useMyLeaveBalances } from './useLeaveBalances';
import { devLog } from '@/utils/logger';

interface ValidationError {
  field: string;
  message: string;
  severity: 'error' | 'warning' | 'info';
}

interface ValidationState {
  errors: ValidationError[];
  isValidating: boolean;
  canSubmit: boolean;
  availableBalance: number;
  requestedDays: number;
  policyName?: string;
  warnings: string[];
}

export type { ValidationError, ValidationState };

export const useLeaveRequestValidation = (
  leaveType: string,
  startDate: string,
  endDate: string,
  requestedHours?: number,
  startTime?: string,
  endTime?: string
) => {
  const { profile } = useAuth();
  const { data: balances } = useMyLeaveBalances();
  
  const [validationState, setValidationState] = useState<ValidationState>({
    errors: [],
    isValidating: false,
    canSubmit: false,
    availableBalance: 0,
    requestedDays: 0,
    policyName: undefined,
    warnings: []
  });

  // Debounce validation calls to avoid excessive API requests
  const debouncedLeaveType = useDebounce(leaveType, 300);
  const debouncedStartDate = useDebounce(startDate, 300);
  const debouncedEndDate = useDebounce(endDate, 300);
  const debouncedStartTime = useDebounce(startTime, 300);
  const debouncedEndTime = useDebounce(endTime, 300);

  const calculateDays = useCallback((start: string, end: string, hours?: number) => {
    if (hours && hours > 0) {
      return hours / 8; // Convert hours to days
    }
    if (!start || !end) return 0;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  }, []);

  const validateRequest = useCallback(async () => {
    devLog('[useLeaveRequestValidation] Step 1: Starting validation');
    devLog('[useLeaveRequestValidation] Inputs:', {
      profileId: profile?.id,
      leaveType: debouncedLeaveType,
      startDate: debouncedStartDate,
      endDate: debouncedEndDate,
      startTime: debouncedStartTime,
      endTime: debouncedEndTime
    });

    if (!profile?.id || !debouncedLeaveType || !debouncedStartDate || !debouncedEndDate) {
      devLog('[useLeaveRequestValidation] Step 2: Missing required inputs, skipping validation');
      setValidationState(prev => ({
        ...prev,
        errors: [],
        isValidating: false,
        canSubmit: false,
        requestedDays: 0
      }));
      return;
    }

    // Basic date validation
    const start = new Date(debouncedStartDate);
    const end = new Date(debouncedEndDate);
    const requestedDays = calculateDays(debouncedStartDate, debouncedEndDate, requestedHours);

    devLog('[useLeaveRequestValidation] Step 3: Calculated requested days:', requestedDays, 'hours:', requestedHours);

    const errors: ValidationError[] = [];

    if (start > end) {
      devLog('[useLeaveRequestValidation] Step 4: Date error - end date before start date');
      errors.push({
        field: 'dates',
        message: 'End date must be after start date',
        severity: 'error'
      });
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (start < today) {
      devLog('[useLeaveRequestValidation] Step 5: Warning - start date in past');
      errors.push({
        field: 'start_date',
        message: 'Start date cannot be in the past',
        severity: 'warning'
      });
    }

    // Time validation for part-day requests
    if (debouncedStartTime && debouncedEndTime) {
      if (debouncedStartTime >= debouncedEndTime) {
        devLog('[useLeaveRequestValidation] Step 5a: Time error - end time before start time');
        errors.push({
          field: 'time',
          message: 'End time must be after start time',
          severity: 'error'
        });
      }
    }

    // Get available balance for this leave type
    // IMPORTANT: Available balance = current_balance - pending_days (to match backend calculation)
    // FIX: Filter by request start year to prevent selecting stale year balances
    const requestYear = debouncedStartDate ? new Date(debouncedStartDate).getFullYear() : new Date().getFullYear();
    const balance = balances?.find(b => 
      b.policy.leave_type === debouncedLeaveType && 
      b.balance_year === requestYear
    );
    const currentBalance = balance?.current_balance_days || 0;
    const pendingDays = balance?.pending_days || 0;
    const availableBalance = currentBalance - pendingDays;
    const policyName = balance?.policy.name;

    devLog('[useLeaveRequestValidation] Step 6: Balance lookup:', {
      leaveType: debouncedLeaveType,
      currentBalance,
      pendingDays,
      availableBalance,
      policyName,
      balancesCount: balances?.length || 0,
      rawBalance: balance
    });

    setValidationState(prev => ({
      ...prev,
      isValidating: true,
      requestedDays,
      availableBalance,
      policyName
    }));

    try {
      devLog('[useLeaveRequestValidation] Step 7: Calling backend validation RPC');
      
      // Call backend validation
      const validationResult = await rolloverService.validateLeaveRequest(
        profile.id,
        debouncedLeaveType,
        debouncedStartDate,
        debouncedEndDate,
        requestedDays
      );

      devLog('[useLeaveRequestValidation] Step 8: Raw backend response:', validationResult);
      devLog('[useLeaveRequestValidation] Step 8b: Response type:', typeof validationResult);
      devLog('[useLeaveRequestValidation] Step 8c: Is array:', Array.isArray(validationResult));

      // Parse validation result - handle TABLE format response
      const backendErrors: ValidationError[] = [];
      const warnings: string[] = [];

      if (validationResult) {
        // Handle array response (RPC returns TABLE)
        if (Array.isArray(validationResult) && validationResult.length > 0) {
          // Cast to typed object
          const firstRow = validationResult[0] as {
            is_valid?: boolean;
            error_code?: string;
            error_message?: string;
            available_balance?: number;
            requested_days?: number;
            policy_id?: string;
            policy_name?: string;
            warnings?: string[];
          };
          devLog('[useLeaveRequestValidation] Step 9: Parsing array response, first row:', firstRow);
          
          if (firstRow.is_valid === false) {
            devLog('[useLeaveRequestValidation] Step 10: Validation FAILED:', {
              error_code: firstRow.error_code,
              error_message: firstRow.error_message
            });
            
            // Map error codes to user-friendly messages
            let userMessage = firstRow.error_message || 'Validation failed';
            const severity: 'error' | 'warning' = 'error';
            
            // Use backend's available_balance if provided (more accurate), otherwise use frontend calculation
            const backendAvailable = firstRow.available_balance ?? availableBalance;
            devLog('[useLeaveRequestValidation] Step 10b: Balance comparison:', {
              frontendAvailable: availableBalance,
              backendAvailable: firstRow.available_balance,
              usingValue: backendAvailable,
              requestedDays
            });
            
            switch (firstRow.error_code) {
              case 'INSUFFICIENT_BALANCE':
                userMessage = `Insufficient leave balance. You have ${backendAvailable} days available but requested ${requestedDays} days.`;
                break;
              case 'NO_POLICY':
                userMessage = `No leave policy found for ${debouncedLeaveType}. Please contact your administrator.`;
                break;
              case 'EXCEEDS_MAX_CARRY_OVER':
                userMessage = firstRow.error_message || 'Request exceeds maximum carry-over limit';
                break;
              case 'NEGATIVE_BALANCE_NOT_ALLOWED':
                userMessage = 'Negative leave balance is not allowed by your policy';
                break;
              default:
                userMessage = firstRow.error_message || `Validation failed: ${firstRow.error_code}`;
            }
            
            backendErrors.push({
              field: 'balance',
              message: userMessage,
              severity: severity
            });
          } else {
            devLog('[useLeaveRequestValidation] Step 10: Validation PASSED');
          }
        }
        // Handle object response (legacy format)
        else if (typeof validationResult === 'object' && !Array.isArray(validationResult)) {
          const result = validationResult as any;
          devLog('[useLeaveRequestValidation] Step 9: Parsing object response:', result);
          
          // Check is_valid field directly
          if (result.is_valid === false) {
            devLog('[useLeaveRequestValidation] Step 10: Object validation FAILED:', {
              error_code: result.error_code,
              error_message: result.error_message
            });
            
            let userMessage = result.error_message || 'Validation failed';
            if (result.error_code === 'INSUFFICIENT_BALANCE') {
              userMessage = `Insufficient leave balance. You have ${availableBalance} days available but requested ${requestedDays} days.`;
            }
            
            backendErrors.push({
              field: 'balance',
              message: userMessage,
              severity: 'error'
            });
          }
          
          // Also check legacy errors array format
          if (result.errors && Array.isArray(result.errors)) {
            devLog('[useLeaveRequestValidation] Step 10b: Found legacy errors array:', result.errors);
            result.errors.forEach((error: string) => {
              backendErrors.push({
                field: 'policy',
                message: error,
                severity: 'error'
              });
            });
          }

          if (result.warnings && Array.isArray(result.warnings)) {
            devLog('[useLeaveRequestValidation] Step 10c: Found warnings:', result.warnings);
            warnings.push(...result.warnings);
          }
        }
      }

      const allErrors = [...errors, ...backendErrors];
      const hasErrors = allErrors.some(e => e.severity === 'error');

      devLog('[useLeaveRequestValidation] Step 11: Final validation state:', {
        totalErrors: allErrors.length,
        hasBlockingErrors: hasErrors,
        canSubmit: !hasErrors && requestedDays > 0,
        errors: allErrors
      });

      setValidationState({
        errors: allErrors,
        isValidating: false,
        canSubmit: !hasErrors && requestedDays > 0,
        availableBalance,
        requestedDays,
        policyName,
        warnings
      });
    } catch (error) {
      console.error('[useLeaveRequestValidation] Step ERROR: Validation failed with exception:', error);
      setValidationState({
        errors: [
          ...errors,
          {
            field: 'validation',
            message: 'Unable to validate request. Please try again.',
            severity: 'warning'
          }
        ],
        isValidating: false,
        canSubmit: errors.length === 0 && requestedDays > 0,
        availableBalance,
        requestedDays,
        policyName,
        warnings: []
      });
    }
  }, [
    profile?.id,
    debouncedLeaveType,
    debouncedStartDate,
    debouncedEndDate,
    debouncedStartTime,
    debouncedEndTime,
    balances,
    calculateDays,
    requestedHours
  ]);

  useEffect(() => {
    validateRequest();
  }, [validateRequest]);

  return validationState;
};
